import classNames from 'classnames';
import type { ReactElement, Ref, RefObject } from 'react';
import { useEffect, useImperativeHandle, useRef, useState } from 'react';
import styles from './WorkFilterVisualItem.module.scss';
import {
  setupScrollTimeline,
  setupTransitionInTimeline,
  setupTransitionOutTimeline,
} from './WorkFilterVisualItem.transitions';
import type {
  WorkFilterVisualItemProps,
  WorkFilterVisualItemRef,
} from './WorkFilterVisualItem.data';
import { useTransitionController } from 'util/react-transition-component';
import Heading from '../../../atoms/heading/Heading';
import type { HeadingRef } from '../../../atoms/heading/Heading.data';
import LottieView from '../../../atoms/lottie-view/LottieView';
import useScrollTrigger from '../../../../hooks/useScrollTrigger';
import useSetupTimeline from '../../../../hooks/useSetupTimeline';
import { useMobileViewport } from '../../../../hooks/useMobileViewport';
import sharedVariables from '../../../../data/shared-variable/shared-variables.json';

function WorkFilterVisualItem({
  title,
  icon,
  ref,
  ...props
}: WorkFilterVisualItemProps & { ref?: Ref<WorkFilterVisualItemRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = ((ref as RefObject<WorkFilterVisualItemRef | null>).current?.elementRef ||
    localRef) as RefObject<HTMLDivElement | null>;
  const titleRef = useRef<HeadingRef>(null);
  const animationRef = useRef<HTMLDivElement>(null);
  const [isActive, setIsActive] = useState(false);
  const scrollTimeline = useRef<gsap.core.Timeline>(undefined);
  const isActiveRef = useRef(false);
  const isMobileViewport = useMobileViewport(sharedVariables.deviceState.MIN_768);

  useEffect(() => {
    isActiveRef.current = isActive;
  }, [isActive]);

  scrollTimeline.current = useSetupTimeline(setupScrollTimeline, {
    titleRef,
    animationRef,
    isMobileViewport,
  });

  useScrollTrigger({
    trigger: titleRef,
    vars: {
      scrub: true,
      start: 'top bottom',
      end: 'top top',
      onUpdate: ({ progress }) => {
        if (isActiveRef.current) {
          scrollTimeline?.current?.progress(progress);
        }
      },
    },
  });

  useTransitionController(
    () => ({
      ref: elementRef,
      refs: {
        elementRef,
        titleRef,
        animationRef,
      },
      setupTransitionInTimeline,
      setupTransitionOutTimeline,
      onStart: (direction) => {
        if (direction === 'in') {
          setIsActive(true);
        }
      },
      onComplete: (direction) => {
        if (direction === 'out') {
          setIsActive(false);
        }
      },
    }),
    [],
  );

  useImperativeHandle(
    ref,
    () => ({
      elementRef,
      animationRef,
    }),
    [elementRef, animationRef],
  );

  return (
    <div ref={elementRef} className={classNames(styles.workFilterVisualItem, props.className)}>
      <Heading
        ref={titleRef}
        as="h2"
        size="heading-01"
        className={styles.title}
        transitionOutType="splitCharsSlideOutUp"
        disableTriggerInOnScroll
      >
        {title.split(' ').map((item, index) => (
          <span key={item}>
            {item} {index === 0 ? <br /> : ' '}
          </span>
        ))}
      </Heading>

      <div ref={animationRef} className={classNames(styles.animationHolder)}>
        <LottieView
          id={icon}
          loop
          autoplay={isActive}
          pauseWhenOutViewport
          className={classNames(styles.animation, 'abs-fill')}
        />
      </div>
    </div>
  );
}

WorkFilterVisualItem.displayName = 'WorkFilterVisualItem';

export default WorkFilterVisualItem;
